import { inject, Injectable } from '@angular/core';
import { isAfter, subMonths } from 'date-fns';
import { RelativeDateTimeService, toPromise } from '@shared/utils';
import {
  ListResponseArgsOfAsyncTaskInfoDto,
  ListResponseArgsOfAsyncTaskLogInfoDto,
  ResponseArgsOfAsyncTaskInfoDto,
  ResponseArgsOfRolloutWorkingTimePatternResultDto,
} from '@swagger/humanresources';
import { map, Observable } from 'rxjs';
import { ContributorScheduleService } from 'src/swagger/humanresources/services/contributor-schedule.service';
import { MyUserAccountService } from 'src/swagger/humanresources/services/my-user-account.service';
import { OrganizationalUnitService } from 'src/swagger/humanresources/services/organizational-unit.service';
import { afterDateForLogs } from './store/rollout.const';
import { RolloutBehaviorSelection } from './rollout-behavior/rollout-behavior.interface';
import { toRolloutScheduleBehavior } from './rollout-behavior/rollout-behavior.util';
import { OrgUnit, RolloutStatusEntry } from './store/rollout.interface';
import { getRolloutStatusFromMilestone, getRolloutStatusFromResults } from './store/rollout.helper';

@Injectable({
  providedIn: 'root',
})
export class RolloutDialogService {
  #organizationalUnitService = inject(OrganizationalUnitService);
  #contributorScheduleService = inject(ContributorScheduleService);
  #myUserAccountService = inject(MyUserAccountService);
  readonly #relativeDateTimeService = inject(RelativeDateTimeService);
  async transformAsyncTasksResponse(
    response: ListResponseArgsOfAsyncTaskInfoDto,
    branches: OrgUnit[],
  ): Promise<RolloutStatusEntry[]> {
    if (!response?.result || !Array.isArray(response.result) || !Array.isArray(branches)) {
      return [];
    }
    const branchUIdMap = new Map(branches.map((branch) => [branch.uId, branch]));
    const latestEntries = new Map<string, RolloutStatusEntry>();

    // Calculate the cutoff date (one month ago from now)
    const oneMonthAgo = subMonths(this.#relativeDateTimeService.getNewDateObject(), 1);
    // const oneMonthAgo = subDays(new Date(), 1);

    for (const item of response.result) {
      // Filter out items that contain "Simulation" in the name (case-insensitive)
      if (item.name && item.name.toLowerCase().includes('simulation')) {
        continue;
      }

      // Only process items with version 2
      if (item.version !== 2) {
        continue;
      }

      // Only process items changed within the last month
      if (!item.changed || !isAfter(new Date(item.changed), oneMonthAgo)) {
        continue;
      }

      const references = item.message?.references;
      if (!references || !Array.isArray(references)) {
        continue;
      }

      for (const reference of references) {
        const referenceUId = reference.data?.referenceUId;

        if (referenceUId && branchUIdMap.has(referenceUId)) {
          const latestMilestone = item.latestMilestone || null;
          const newEntry: RolloutStatusEntry = {
            ouUId: referenceUId,
            latestMilestone: latestMilestone,
            referenceLogsUId: item.uId,
            changed: item.changed,
            rolloutStatus: getRolloutStatusFromMilestone(latestMilestone),
          };

          const existingEntry = latestEntries.get(referenceUId);

          if (existingEntry) {
            const existingDate = new Date(existingEntry.changed);
            const newDate = new Date(newEntry.changed);

            if (newDate > existingDate) {
              // console.warn(
              //   `Duplicate ouUId found: ${referenceUId}. Replacing older entry (${existingEntry.changed}) with newer entry (${newEntry.changed})`,
              // );
              latestEntries.set(referenceUId, newEntry);
            } else {
              // console.warn(
              //   `Duplicate ouUId found: ${referenceUId}. Keeping existing entry (${existingEntry.changed}) over older entry (${newEntry.changed})`,
              // );
            }
          } else {
            latestEntries.set(referenceUId, newEntry);
          }

          break;
        }
      }
    }

    // After processing all entries, fetch log data for entries with specific milestones
    const entriesWithLogs = Array.from(latestEntries.values());

    // Process entries with milestones 1, 2, or 4 to fetch log data
    const logFetchPromises = entriesWithLogs
      .filter(
        (entry) =>
          entry.latestMilestone === 1 || entry.latestMilestone === 2 || entry.latestMilestone === 4,
      )
      .map(async (entry) => {
        try {
          const logResponse = await this.fetchAsyncTaskLogs(entry.referenceLogsUId);

          if (logResponse?.result?.length > 0) {
            const firstResult = logResponse.result[0];
            if (firstResult?.jsonMessage) {
              // Parse the stringified JSON message
              const parsedMessage = JSON.parse(firstResult.jsonMessage);

              // Attach the parsed log response to the entry
              entry.logResponse = {
                Error: parsedMessage.Error || false,
                Completed: parsedMessage.Completed || false,
                Total: parsedMessage.Total || 0,
                Successful: parsedMessage.Successful || [],
                Failed: parsedMessage.Failed || [],
              };

              // Update rollout status based on actual results
              entry.rolloutStatus = getRolloutStatusFromResults(
                entry.logResponse.Successful,
                entry.logResponse.Failed,
                entry.latestMilestone,
              );
            }
          }
        } catch (error) {
          console.error(`Failed to fetch logs for entry ${entry.ouUId}:`, error);
          // Don't set logResponse on error, leave it undefined
        }
        return entry;
      });

    // Wait for all log fetch operations to complete
    await Promise.all(logFetchPromises);

    return entriesWithLogs;
  }

  async fetchAsyncTasks(branches): Promise<RolloutStatusEntry[] | null> {
    return toPromise(
      this.#myUserAccountService
        .myUserAccountListAsyncTasks({
          eagerLoading: 3,
        })
        .pipe(
          map(async (response) => {
            const result = await this.transformAsyncTasksResponse(response, branches);
            return result;
          }),
        ),
    );
  }
  fetchAsyncTaskLogs(asyncTaskUId: string): Promise<ListResponseArgsOfAsyncTaskLogInfoDto> {
    return toPromise(
      this.#myUserAccountService.myUserAccountGetAsyncTaskLogs({
        asyncTaskUId: asyncTaskUId,
        onlyMilestones: true,
        after: afterDateForLogs,
      }),
    );
  }

  rolloutOu(
    ouUids: string[],
    targetStart: string,
    targetStop: string,
    rolloutBehavior: RolloutBehaviorSelection,
  ): Observable<ResponseArgsOfAsyncTaskInfoDto> {
    const payload = {
      body: {
        behavior: toRolloutScheduleBehavior(rolloutBehavior),
        dryRun: false,
        ouUids,
        targetStart,
        targetStop,
      },
    };

    return this.#organizationalUnitService.organizationalUnitRolloutWorkingTimePatterns(payload);
  }

  rolloutContributor(
    contributorUId: string,
    scheduleUId: string,
    workScheduleUid: string,
    startAtWeek: number,
    targetStart: string,
    targetStop: string,
    rolloutBehavior: RolloutBehaviorSelection,
  ): Observable<ResponseArgsOfRolloutWorkingTimePatternResultDto> {
    const payload = {
      contributorUId: contributorUId || '',
      scheduleUId: scheduleUId || '',
      body: {
        behavior: toRolloutScheduleBehavior(rolloutBehavior),
        startAtWeek: startAtWeek,
        targetScheduleUId: workScheduleUid,
        targetStart,
        targetStop,
      },
    };
    return this.#contributorScheduleService.contributorScheduleRolloutWorkingTimePattern(payload);
  }
}
